import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchFolderAncestors,
  fetchFolderContents,
  normalizeLimit,
} from '../api';
import {
  type DocumentContentsPageParam,
  type FolderContentsParams,
} from '../types';

export const folderContentsKeys = {
  all: ['documents', 'folderContents'] as const,
  lists: () => [...folderContentsKeys.all, 'list'] as const,
  list: (
    companyId: string,
    folderId: string | undefined,
    params?: FolderContentsParams
  ) =>
    [
      ...folderContentsKeys.lists(),
      companyId,
      folderId ?? 'root',
      params,
    ] as const,
  infinite: (
    companyId: string,
    folderId: string | undefined,
    params?: Pick<FolderContentsParams, 'limit' | 'search'>
  ) =>
    [
      ...folderContentsKeys.all,
      'infinite',
      companyId,
      folderId ?? 'root',
      {
        limit: normalizeLimit(params?.limit),
        search: params?.search,
      },
    ] as const,
  ancestors: (folderId: string) =>
    [...folderContentsKeys.all, 'ancestors', folderId] as const,
};

export function useFolderContents(
  companyId: string | null,
  folderId?: string | null,
  params?: FolderContentsParams
) {
  const isValidCompanyId = companyId !== null && companyId.length > 0;
  return useQuery({
    queryKey: folderContentsKeys.list(companyId ?? '', folderId ?? undefined, params),
    queryFn: () => fetchFolderContents(companyId!, folderId ?? undefined, params),
    enabled: isValidCompanyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
}

export function useInfiniteDocumentContents(
  companyId: string | null,
  folderId?: string,
  params?: Pick<FolderContentsParams, 'limit' | 'search'>
) {
  const limit = normalizeLimit(params?.limit);
  const isValidCompanyId = companyId !== null && companyId.length > 0;

  return useInfiniteQuery({
    queryKey: folderContentsKeys.infinite(companyId ?? '', folderId, {
      limit,
      search: params?.search,
    }),
    queryFn: ({ pageParam }) =>
      fetchFolderContents(companyId!, folderId, {
        limit,
        search: params?.search,
        folderCursor: pageParam.folderCursor,
        fileCursor: pageParam.fileCursor,
      }),
    enabled: isValidCompanyId,
    initialPageParam: {} as DocumentContentsPageParam,
    placeholderData: (previousData) => previousData,
    getNextPageParam: (lastPage) => {
      if (!lastPage.folderNextCursor && !lastPage.fileNextCursor) {
        return undefined;
      }

      return {
        folderCursor: lastPage.folderNextCursor ?? undefined,
        fileCursor: lastPage.fileNextCursor ?? undefined,
      } satisfies DocumentContentsPageParam;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
}

export function useFolderAncestors(folderId?: string, enabled = true) {
  return useQuery({
    queryKey: folderId
      ? folderContentsKeys.ancestors(folderId)
      : [...folderContentsKeys.all, 'ancestors', 'none'],
    queryFn: () => fetchFolderAncestors(folderId!),
    enabled: enabled && Boolean(folderId),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}
